import React from 'react'
import { Box, Typography } from 'components/Material/index'
import useSidebarStore from 'store/useSidebar'
import useTextStyles from 'styles/useTextStyles'
import { ApplyFiltersButton, SelectField } from 'components/Custom/index'
import IconButton from 'components/Material/IconButton'
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined'
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined'
import useSortHeaderStore from 'store/useSortHeaderStore'

type SortItem = {
  value: string
  label: string
}

type SortHeaderProps = {
  title?: string
  qnt_found?: number
  isLoading?: boolean
  isSelecField?: boolean
  sortItems?: SortItem[]
  hasFilterButton?: string
  onSelectChange?: (value: string) => void
  orderSelected?: string
  customClassNameHeader?: string
  showVisualizationButtons?: boolean
  ButtonInclude?: React.ReactNode
  hideSortHeader?: boolean
}

export const SortHeader: React.FC<SortHeaderProps> = ({
  title,
  qnt_found,
  isSelecField = true,
  sortItems,
  hasFilterButton,
  isLoading = false,
  onSelectChange,
  orderSelected,
  hideSortHeader,
  customClassNameHeader,
  showVisualizationButtons = false,
  ButtonInclude = null
}) => {
  const { openSidebar } = useSidebarStore()
  const { selectedView, setSelectedView } = useSortHeaderStore((state) => state)

  const handleSelectChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string

    if (onSelectChange && isValidSortOrderKey(value)) {
      onSelectChange(value)
    }
  }

  const validSortOrderKeys: string[] = sortItems ? sortItems.map((item) => item.value) : []

  function isValidSortOrderKey(value: string) {
    return validSortOrderKeys.includes(value)
  }

  return (
    <Box className="bg-white border-b">
      <Box className={`px-5 ${isSelecField && 'pt-5'} space-y-5 ${customClassNameHeader}`}>
        {/* Primeira linha: Título + Select (desktop) / Botões de visualização + Botão de Filtros */}
        <Box className="z-10 flex flex-wrap items-center justify-between gap-5">
          <Box className="flex-1">
            <Box className="leading-1">
              <Typography sx={{ ...useTextStyles.headlineLarge }}>{title}</Typography>
            </Box>
          </Box>

          <Box className="flex items-center gap-5">
            {/* Botões de visualização (se existirem) */}
            {showVisualizationButtons && (
              <Box title="Visualização" className="flex gap-2">
                <IconButton
                  title="Tabela"
                  sx={{
                    border: `1px solid ${selectedView === 'table' ? '#007bff' : '#79747E'}`,
                    borderRadius: '8px'
                  }}
                  onClick={() => setSelectedView('table')}
                  aria-label="Tabela"
                >
                  <TableRowsOutlinedIcon
                    sx={{ color: selectedView === 'table' ? '#007bff' : '#79747E' }}
                  />
                </IconButton>
                <IconButton
                  title="Cards"
                  sx={{
                    border: `1px solid ${selectedView === 'cards' ? '#007bff' : '#79747E'}`,
                    borderRadius: '8px'
                  }}
                  onClick={() => setSelectedView('cards')}
                  aria-label="Cards"
                >
                  <DashboardOutlinedIcon
                    sx={{ color: selectedView === 'cards' ? '#007bff' : '#79747E' }}
                  />
                </IconButton>
              </Box>
            )}

            {/* Select de ordenar - escondido em mobile, visível em desktop */}
            {isSelecField && !hideSortHeader && (
              <Box className="hidden lg:block">
                <SelectField
                  title="Ordenar por"
                  value={orderSelected ?? ''}
                  className="w-[180px] font-bold"
                  description="Ordenar por"
                  menuItems={sortItems || []}
                  onChange={handleSelectChange}
                />
              </Box>
            )}

            {/* Botão de filtros */}
            {hasFilterButton && (
              <Box>
                <ApplyFiltersButton onClick={() => openSidebar(hasFilterButton)} />
              </Box>
            )}
          </Box>
        </Box>

        {/* Segunda linha: Select de ordenar - visível apenas em mobile */}
        {isSelecField && !hideSortHeader && (
          <Box className="flex justify-end items-center gap-5 w-full lg:hidden">
            <Box>
              <SelectField
                title="Ordenar por"
                value={orderSelected ?? ''}
                className="w-[180px] font-bold"
                description="Ordenar por"
                menuItems={sortItems || []}
                onChange={handleSelectChange}
              />
            </Box>
          </Box>
        )}
        {ButtonInclude && (
          <Box title="Incluir" className="w-full flex lg:justify-end justify-center">
            {ButtonInclude}
          </Box>
        )}
        <Box className="flex justify-center pb-5">
          {!isLoading && (
            <Typography sx={{ ...useTextStyles.titleSmall }}>
              {qnt_found !== 0 ? <>{qnt_found} Encontrados(as)</> : 'Nenhum item encontrado.'}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}
